import {
  ChakraProvider,
  ColorModeScript,
  createLocalStorageManager,
} from "@chakra-ui/react";
import * as React from "react";
import * as ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { App } from "./App";
import { Amplify } from "aws-amplify";
import ReactGA from "react-ga4";
import "./index.css";
import theme, { colourModeKey } from "./theme";
import { BrowserRouter as Router } from "react-router-dom";
import { AppProvider } from "./context/app.context";

// Constants
const SENTRY_DSN =
  "https://4fcd5f1cf2204b5d8930e86937672d6e@itpiapi.lancaster.ac.uk:9000/18";
const GOOGLE_TRACKING_ID = "G-R9ZK1FTL96";

if (new URLSearchParams(window.location.search).get("loginType") === "public") {
  localStorage.setItem("ITPI_GLOBAL_LOGIN_METHOD", "public");
}

const loginType =
  localStorage.getItem("ITPI_GLOBAL_LOGIN_METHOD") || "lancaster";
let domain: string;
let userPoolWebClientId: string;
let userPoolId: string;

if (process.env.REACT_APP_ENV === "production") {
  domain =
    loginType === "lancaster"
      ? "itpiauth.lancaster-university.uk"
      : "https://open-ilancaster-production.auth.eu-west-2.amazoncognito.com";
  userPoolWebClientId =
    loginType === "lancaster"
      ? "5vfh3lfvl1ofe6tgs0tb4jml63"
      : "6d4ihgh788p0e1bkup9rmbuirp";
  userPoolId =
    loginType === "lancaster" ? "eu-west-2_OfWH8KgOK" : "eu-west-2_VgFyZT27o";
} else {
  domain =
    loginType === "lancaster"
      ? "ilancaster-voice-stg.auth.eu-west-2.amazoncognito.com"
      : "open-ilancaster-staging.auth.eu-west-2.amazoncognito.com";
  userPoolWebClientId =
    loginType === "lancaster"
      ? "nm7c31hbc3u7mlh9ua4tple6u"
      : "3qr7956k2p29ompa46oqb9slrt";
  userPoolId =
    loginType === "lancaster" ? "eu-west-2_OEzkjWLXx" : "eu-west-2_3DDr6a54j";
}

// AWS Cognito Configuration
Amplify.configure({
  Auth: {
    region: "eu-west-2",
    userPoolId,
    userPoolWebClientId,
    mandatorySignIn: false,
    oauth: {
      domain: domain,
      scope: ["profile", "openid"],
      redirectSignIn: process.env.REACT_APP_DEPLOYMENT_URL
        ? process.env.REACT_APP_DEPLOYMENT_URL + "/"
        : "http://localhost:3000",
      // redirectSignOut: "https://weblogin.lancs.ac.uk/logout",
      responseType: "code",
    },
  },
});

// Sentry configuration
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN, //TODO replace with real dsn
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.75,
  });

  // Intercept all XHR requests and report the error on sentry
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function () {
    this.addEventListener("load", function () {
      if (!this.status.toString().startsWith("2")) {
        const error = new Error(this.statusText);
        Sentry.addBreadcrumb({
          category: "api",
          message: `Handling API non-2xx response ${this.responseURL}`,
          level: "error",
        });
        Sentry.captureException(error);
      }
    });
    origOpen.apply(this, arguments as any);
  };
} else {
  console.warn("Sentry DSN is not configured!");
}

// ReactGA4 Configuration
if (GOOGLE_TRACKING_ID) {
  ReactGA.initialize(GOOGLE_TRACKING_ID);
} else {
  console.warn("Google Analytics Tracking ID is not configured!");
}

// Render React App
const container = document.getElementById("root");
if (!container) throw new Error("Failed to find the root element");
const root = ReactDOM.createRoot(container);

const manager = createLocalStorageManager(colourModeKey);

root.render(
  <React.StrictMode>
    <ColorModeScript storageKey={colourModeKey} />
    <ChakraProvider theme={theme} colorModeManager={manager}>
      <Router basename={process.env.REACT_APP_BASENAME}>
        <AppProvider>
          <App />
        </AppProvider>
      </Router>
    </ChakraProvider>
  </React.StrictMode>
);
